/**
 * The Board Document — docs/05-DATA-MODEL.md §3. FROZEN.
 *
 * BoardDoc (Y.Doc)
 * ├── meta     Y.Map     title, topic, gradeBand, createdAt, schemaVersion
 * ├── pages    Y.Array<Y.Map>
 * │     └── page: id, name, order, background Y.Map, strokes Y.Array, objects Y.Array
 * └── awareness           cursors, tools, names — NEVER persisted
 *
 * Why a CRDT: teacher and student drawing in the same place cannot corrupt the board,
 * offline edits merge correctly on reconnect, and changes apply locally and instantly
 * so ink never waits for a 250ms Pakistan-to-USA round trip.
 */
import * as Y from 'yjs';
import { newObjectId, newPageId, newStrokeId, } from '@opal/shared';
/** Bump only with a migration plan for every saved lecture. */
export const BOARD_SCHEMA_VERSION = 1;
export const META_KEY = 'meta';
export const PAGES_KEY = 'pages';
export const PAGE_STROKES_KEY = 'strokes';
export const PAGE_OBJECTS_KEY = 'objects';
export const PAGE_BACKGROUND_KEY = 'background';
/** SRS C1 — a warning banner appears past this many pages. */
export const PAGE_COUNT_WARNING_THRESHOLD = 30;
export const DEFAULT_BACKGROUND = {
    type: 'blank',
    gridSize: 40,
    color: '#E2E2EA',
};
export function toStoredStroke(stroke) {
    return { ...stroke, points: Array.from(stroke.points) };
}
export function fromStoredStroke(stored) {
    return { ...stored, points: Float32Array.from(stored.points) };
}
/**
 * Objects are stored as a Y.Map each, not as plain values.
 *
 * Two people dragging the same badge at the same moment is normal in a lesson. A map
 * per object merges those edits field by field, so one person moving it while another
 * resizes it keeps both changes instead of one silently replacing the other.
 */
export function writeObject(object) {
    const map = new Y.Map();
    for (const [key, value] of Object.entries(object))
        map.set(key, value);
    return map;
}
export function readObject(map) {
    return {
        id: map.get('id'),
        kind: map.get('kind'),
        createdByModule: map.get('createdByModule'),
        userId: map.get('userId'),
        x: map.get('x'),
        y: map.get('y'),
        width: map.get('width'),
        height: map.get('height'),
        rotation: map.get('rotation'),
        zIndex: map.get('zIndex'),
        layer: map.get('layer'),
        locked: map.get('locked'),
        data: map.get('data') ?? {},
        createdAt: map.get('createdAt'),
        updatedAt: map.get('updatedAt'),
    };
}
/** Build a brand-new board document with one empty page. */
export function createBoardDoc(input = {}) {
    const doc = new Y.Doc();
    doc.transact(() => {
        const meta = doc.getMap(META_KEY);
        meta.set('title', input.title ?? 'Untitled Board');
        meta.set('topic', input.topic ?? null);
        meta.set('gradeBand', input.gradeBand ?? 'middle');
        meta.set('createdAt', Date.now());
        meta.set('schemaVersion', BOARD_SCHEMA_VERSION);
        const pages = doc.getArray(PAGES_KEY);
        pages.push([buildPage(input.firstPageName ?? 'Page 1', 0)]);
    });
    return doc;
}
/** An empty page, ready to be pushed into the pages array. */
export function buildPage(name, order) {
    const page = new Y.Map();
    page.set('id', newPageId());
    page.set('name', name);
    page.set('order', order);
    const background = new Y.Map();
    background.set('type', DEFAULT_BACKGROUND.type);
    background.set('gridSize', DEFAULT_BACKGROUND.gridSize);
    background.set('color', DEFAULT_BACKGROUND.color);
    page.set(PAGE_BACKGROUND_KEY, background);
    page.set(PAGE_STROKES_KEY, new Y.Array());
    page.set(PAGE_OBJECTS_KEY, new Y.Array());
    return page;
}
/**
 * A page written out again somewhere else: same paper, same work.
 *
 * Yjs holds a shared type in exactly one place, so a page cannot be moved or copied
 * by reference — everything on it has to be built afresh. `freshIds` is what separates
 * the two callers: moving a page keeps every id, because it is the same page in a new
 * place; copying one must not, because two pages sharing a stroke id would erase in
 * lockstep.
 */
export function clonePage(page, name, freshIds = false) {
    const copy = new Y.Map();
    copy.set('id', freshIds ? newPageId() : page.get('id'));
    copy.set('name', name);
    copy.set('order', page.get('order'));
    const paper = new Y.Map();
    const background = getBackground(page);
    paper.set('type', background.type);
    paper.set('gridSize', background.gridSize);
    paper.set('color', background.color);
    copy.set(PAGE_BACKGROUND_KEY, paper);
    const strokes = new Y.Array();
    strokes.push(getStrokes(page)
        .toArray()
        .map((stored) => ({
        ...stored,
        ...(freshIds ? { id: newStrokeId() } : {}),
        points: [...stored.points],
    })));
    copy.set(PAGE_STROKES_KEY, strokes);
    const objects = new Y.Array();
    objects.push(getObjects(page)
        .toArray()
        .map((map) => {
        const object = readObject(map);
        return writeObject(freshIds ? { ...object, id: newObjectId() } : object);
    }));
    copy.set(PAGE_OBJECTS_KEY, objects);
    return copy;
}
// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------
export function getMeta(doc) {
    const meta = doc.getMap(META_KEY);
    return {
        title: meta.get('title') ?? 'Untitled Board',
        topic: meta.get('topic') ?? null,
        gradeBand: meta.get('gradeBand') ?? 'middle',
        createdAt: meta.get('createdAt') ?? 0,
        schemaVersion: meta.get('schemaVersion') ?? BOARD_SCHEMA_VERSION,
    };
}
export function getPages(doc) {
    return doc.getArray(PAGES_KEY);
}
export function getPageSummaries(doc) {
    return getPages(doc)
        .toArray()
        .map((page) => ({
        id: page.get('id'),
        name: page.get('name'),
        order: page.get('order'),
    }))
        .sort((a, b) => a.order - b.order);
}
export function findPage(doc, pageId) {
    return getPages(doc)
        .toArray()
        .find((page) => page.get('id') === pageId) ?? null;
}
export function getStrokes(page) {
    return page.get(PAGE_STROKES_KEY);
}
export function getObjects(page) {
    return page.get(PAGE_OBJECTS_KEY);
}
export function getBackground(page) {
    const background = page.get(PAGE_BACKGROUND_KEY);
    if (!background)
        return DEFAULT_BACKGROUND;
    return {
        type: background.get('type') ?? DEFAULT_BACKGROUND.type,
        gridSize: background.get('gridSize') ?? DEFAULT_BACKGROUND.gridSize,
        color: background.get('color') ?? DEFAULT_BACKGROUND.color,
    };
}
// ---------------------------------------------------------------------------
// Snapshots
// ---------------------------------------------------------------------------
/** Full document state, for board_snapshots and the .opalboard package. */
export function encodeSnapshot(doc) {
    return Y.encodeStateAsUpdate(doc);
}
/** Rebuild a document from a stored snapshot. */
export function decodeSnapshot(update) {
    const doc = new Y.Doc();
    Y.applyUpdate(doc, update);
    return doc;
}
/**
 * Whether this build can open a document.
 *
 * Refuse to open something newer than we understand rather than partially loading it
 * and silently dropping content (docs/05-DATA-MODEL.md §4).
 */
export function canOpen(schemaVersion) {
    return schemaVersion <= BOARD_SCHEMA_VERSION;
}
//# sourceMappingURL=schema.js.map